import React, { useEffect, useMemo, useState } from "react";

const API_BASE = process.env.REACT_APP_NAV_API || "http://localhost:8080";

const POST_TYPES = [
  { value: "OVERALL_ACHIEVEMENT", label: "Overall Achievement" },
  { value: "SALES_ACHIEVEMENT", label: "Sales Achievement" },
  { value: "ANNOUNCEMENT", label: "Announcement (Marquee)" },
];

const STATUS_OPTIONS = [
  { value: "DRAFT", label: "Draft" },
  { value: "PUBLISHED", label: "Published" },
  { value: "ARCHIVED", label: "Archived" },
];

function inputStyle() {
  return {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid rgba(0,0,0,0.12)",
    outline: "none",
    fontWeight: 700,
  };
}

function labelStyle() {
  return { fontSize: 12, fontWeight: 900, color: "#26428b", marginBottom: 6 };
}

export default function AdminPostsPage() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [posts, setPosts] = useState([]);

  const [form, setForm] = useState({
    post_type: "ANNOUNCEMENT",
    title: "",
    description: "",
    status: "PUBLISHED",

    person_name: "",
    department: "",
    location: "",

    cover_image_url: "",
    icon_key: "",

    is_pinned: false,
    pinned_order: 1,
    publish_from: "", // optional ISO string
    publish_to: "",   // optional ISO string

    // payload fields (we store in payload JSON)
    priority: "HIGH",
    action_url: "",
    button_text: "View",
    // sales payload sample
    month: "",
    product: "",
    amount: "",
    unit: "INR",
    rank: "",
  });

  const isAnnouncement = form.post_type === "ANNOUNCEMENT";
  const isSales = form.post_type === "SALES_ACHIEVEMENT";

  const payloadPreview = useMemo(() => {
    if (isAnnouncement) {
      return {
        priority: form.priority || "MEDIUM",
        action_url: form.action_url || null,
        button_text: form.button_text || "View",
      };
    }
    if (isSales) {
      // keep only non-empty fields
      const obj = {
        month: form.month || null,
        product: form.product || null,
        amount: form.amount ? Number(form.amount) : null,
        unit: form.unit || "INR",
        rank: form.rank || null,
      };
      return obj;
    }
    return null;
  }, [form, isAnnouncement, isSales]);

  async function fetchPosts() {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/posts?limit=50&includeExpired=true`, {
  credentials: "include",   // ✅ ADD THIS
});
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "Failed to load posts");
      setPosts(Array.isArray(json.data) ? json.data : []);
    } catch (e) {
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setField(k, v) {
    setForm((prev) => ({ ...prev, [k]: v }));
  }

  function buildPayload() {
    // Only attach payload if needed
    if (isAnnouncement || isSales) return payloadPreview;
    return null;
  }

  async function submit() {
    setSaving(true);
    setError("");
    try {
      if (!form.title.trim()) throw new Error("Title is required");
      if (!form.description.trim()) throw new Error("Description is required");

      const body = {
        post_type: form.post_type,
        title: form.title.trim(),
        description: form.description.trim(),
        status: form.status,

        person_name: form.person_name.trim() || null,
        department: form.department.trim() || null,
        location: form.location.trim() || null,

        cover_image_url: form.cover_image_url.trim() || null,
        icon_key: form.icon_key.trim() || null,

        is_pinned: isAnnouncement ? !!form.is_pinned : false,
        pinned_order: isAnnouncement ? Number(form.pinned_order || 1) : null,
        publish_from: isAnnouncement && form.publish_from ? form.publish_from : null,
        publish_to: isAnnouncement && form.publish_to ? form.publish_to : null,

        payload: buildPayload(),
        // created_by_email will be replaced later by auth token
        created_by_email: "admin@geplcapital.com",
      };

      const res = await fetch(`${API_BASE}/posts`, {
        method: "POST",
        credentials: "include",  
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "Create failed");

      // reset minimal fields
      setForm((prev) => ({
        ...prev,
        title: "",
        description: "",
        cover_image_url: "",
      }));

      await fetchPosts();
      alert("Post created ✅");
    } catch (e) {
      setError(e.message || "Failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 900, color: "#26428b" }}>
          Create Homepage Post
        </div>
        <button
          onClick={fetchPosts}
          style={{
            marginLeft: "auto",
            padding: "8px 10px",
            borderRadius: 10,
            border: "1px solid rgba(0,0,0,0.12)",
            background: "#fff",
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          Refresh
        </button>
      </div>

      {error ? (
        <div
          style={{
            marginBottom: 10,
            padding: 10,
            borderRadius: 12,
            background: "rgba(255,0,0,0.08)",
            color: "#b00020",
            fontWeight: 800,
          }}
        >
          {error}
        </div>
      ) : null}

      {/* FORM */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <div>
          <div style={labelStyle()}>Post Type</div>
          <select
            value={form.post_type}
            onChange={(e) => setField("post_type", e.target.value)}
            style={inputStyle()}
          >
            {POST_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div style={labelStyle()}>Status</div>
          <select
            value={form.status}
            onChange={(e) => setField("status", e.target.value)}
            style={inputStyle()}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <div style={{ gridColumn: "1 / span 2" }}>
          <div style={labelStyle()}>Title</div>
          <input
            value={form.title}
            onChange={(e) => setField("title", e.target.value)}
            style={inputStyle()}
            placeholder="Title"
          />
        </div>

        <div style={{ gridColumn: "1 / span 2" }}>
          <div style={labelStyle()}>Description</div>
          <textarea
            value={form.description}
            onChange={(e) => setField("description", e.target.value)}
            style={{ ...inputStyle(), minHeight: 90, resize: "vertical" }}
            placeholder="Description"
          />
        </div>

        {/* Optional common fields */}
        <div>
          <div style={labelStyle()}>Person Name (optional)</div>
          <input
            value={form.person_name}
            onChange={(e) => setField("person_name", e.target.value)}
            style={inputStyle()}
            placeholder="Name / Team"
          />
        </div>

        <div>
          <div style={labelStyle()}>Department (optional)</div>
          <input
            value={form.department}
            onChange={(e) => setField("department", e.target.value)}
            style={inputStyle()}
            placeholder="Department"
          />
        </div>

        <div>
          <div style={labelStyle()}>Location (optional)</div>
          <input
            value={form.location}
            onChange={(e) => setField("location", e.target.value)}
            style={inputStyle()}
            placeholder="Location"
          />
        </div>

        <div>
          <div style={labelStyle()}>Icon Key (optional)</div>
          <input
            value={form.icon_key}
            onChange={(e) => setField("icon_key", e.target.value)}
            style={inputStyle()}
            placeholder="trophy / star / medal / megaphone"
          />
        </div>

        <div style={{ gridColumn: "1 / span 2" }}>
          <div style={labelStyle()}>Cover Image URL (optional)</div>
          <input
            value={form.cover_image_url}
            onChange={(e) => setField("cover_image_url", e.target.value)}
            style={inputStyle()}
            placeholder="https://..."
          />
        </div>

        {/* Announcement extras */}
        {isAnnouncement ? (
          <>
            <div>
              <div style={labelStyle()}>Pinned?</div>
              <select
                value={String(form.is_pinned)}
                onChange={(e) => setField("is_pinned", e.target.value === "true")}
                style={inputStyle()}
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>

            <div>
              <div style={labelStyle()}>Pinned Order</div>
              <input
                type="number"
                value={form.pinned_order}
                onChange={(e) => setField("pinned_order", e.target.value)}
                style={inputStyle()}
                min={1}
              />
            </div>

            <div>
              <div style={labelStyle()}>Publish From (optional)</div>
              <input
                value={form.publish_from}
                onChange={(e) => setField("publish_from", e.target.value)}
                style={inputStyle()}
                placeholder="2026-03-04T09:30:00Z"
              />
            </div>

            <div>
              <div style={labelStyle()}>Publish To (optional)</div>
              <input
                value={form.publish_to}
                onChange={(e) => setField("publish_to", e.target.value)}
                style={inputStyle()}
                placeholder="2026-03-10T18:00:00Z"
              />
            </div>

            <div>
              <div style={labelStyle()}>Priority</div>
              <select
                value={form.priority}
                onChange={(e) => setField("priority", e.target.value)}
                style={inputStyle()}
              >
                <option value="HIGH">HIGH</option>
                <option value="MEDIUM">MEDIUM</option>
                <option value="LOW">LOW</option>
              </select>
            </div>

            <div>
              <div style={labelStyle()}>Button Text</div>
              <input
                value={form.button_text}
                onChange={(e) => setField("button_text", e.target.value)}
                style={inputStyle()}
                placeholder="View"
              />
            </div>

            <div style={{ gridColumn: "1 / span 2" }}>
              <div style={labelStyle()}>Action URL (optional)</div>
              <input
                value={form.action_url}
                onChange={(e) => setField("action_url", e.target.value)}
                style={inputStyle()}
                placeholder="https://..."
              />
            </div>
          </>
        ) : null}

        {/* Sales extras */}
        {isSales ? (
          <>
            <div>
              <div style={labelStyle()}>Month</div>
              <input
                value={form.month}
                onChange={(e) => setField("month", e.target.value)}
                style={inputStyle()}
                placeholder="Mar-2026"
              />
            </div>
            <div>
              <div style={labelStyle()}>Product</div>
              <input
                value={form.product}
                onChange={(e) => setField("product", e.target.value)}
                style={inputStyle()}
                placeholder="Equity / MF / Insurance..."
              />
            </div>
            <div>
              <div style={labelStyle()}>Amount</div>
              <input
                type="number"
                value={form.amount}
                onChange={(e) => setField("amount", e.target.value)}
                style={inputStyle()}
                placeholder="1250000"
              />
            </div>
            <div>
              <div style={labelStyle()}>Unit</div>
              <input
                value={form.unit}
                onChange={(e) => setField("unit", e.target.value)}
                style={inputStyle()}
                placeholder="INR"
              />
            </div>
            <div style={{ gridColumn: "1 / span 2" }}>
              <div style={labelStyle()}>Rank (optional)</div>
              <input
                value={form.rank}
                onChange={(e) => setField("rank", e.target.value)}
                style={inputStyle()}
                placeholder="Top 1 / Top Performer"
              />
            </div>
          </>
        ) : null}
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 18 }}>
        <button
          onClick={submit}
          disabled={saving}
          style={{
            padding: "10px 14px",
            borderRadius: 12,
            border: "none",
            background: "#26428b",
            color: "#fff",
            fontWeight: 900,
            cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? "Saving..." : "Create Post"}
        </button>

        <div style={{ fontSize: 12, fontWeight: 800, color: "rgba(0,0,0,0.55)" }}>
          Payload Preview: {payloadPreview ? JSON.stringify(payloadPreview) : "null"}
        </div>
      </div>

      {/* RECENT POSTS */}
      <div style={{ fontSize: 15, fontWeight: 900, color: "#26428b", marginBottom: 10 }}>
        Recent Posts
      </div>

      {loading ? (
        <div style={{ padding: 12, fontWeight: 800 }}>Loading...</div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {posts.map((p) => (
            <div
              key={p.post_id}
              style={{
                padding: 12,
                borderRadius: 14,
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
              }}
            >
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <div style={{ fontWeight: 900, color: "#26428b" }}>
                  {p.title}
                </div>
                <div style={{ marginLeft: "auto", fontWeight: 900, fontSize: 12 }}>
                  {p.post_type} • {p.status}
                </div>
              </div>

              <div style={{ marginTop: 6, fontWeight: 700, color: "rgba(0,0,0,0.75)" }}>
                {p.description}
              </div>

              {p.payload ? (
                <div style={{ marginTop: 8, fontSize: 12, fontWeight: 800, color: "rgba(0,0,0,0.6)" }}>
                  payload: {JSON.stringify(p.payload)}
                </div>
              ) : null}
            </div>
          ))}

          {!posts.length ? (
            <div style={{ padding: 12, fontWeight: 800, color: "rgba(0,0,0,0.6)" }}>
              No posts yet.
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}